import React from 'react'
import PropTypes from 'prop-types'

import Input from 'components/Form/Input'
import Rating from 'components/Form/Rating'
import QuizAnswerOption from 'components/QuizAnswerOption'
import {
  SURVEY_QUESTION_TYPES,
} from 'constants/survey'

import {
  ChoiceList,
  ErrorText,
  FieldBody,
  OptionalLabel,
  QuestionFieldset,
  QuestionLegend,
  ScaleButton,
  ScaleGrid,
} from './styled'

export default function SurveyQuestion({
  disabled,
  error,
  focusRef,
  onChange,
  optionalLabel,
  placeholder,
  question,
  requiredLabel,
  value,
}) {
  return (
    <QuestionFieldset disabled={disabled} ref={focusRef} tabIndex="-1">
      <QuestionLegend>
        {question?.text}
        <OptionalLabel>
          {question?.required ? requiredLabel : optionalLabel}
        </OptionalLabel>
      </QuestionLegend>

      <FieldBody>
        {question?.type === SURVEY_QUESTION_TYPES.STAR_RATING ? (
          <Rating
            compact
            disabled={disabled}
            label={question?.text}
            max={question?.maximum || 5}
            value={Number(value || 0)}
            onChange={onChange}
          />
        ) : question?.type === SURVEY_QUESTION_TYPES.NUMERIC_SCALE ? (
          <ScaleGrid>
            {Array.from({
              length: Math.max(0, Number(question?.maximum || 5) - Number(question?.minimum || 1) + 1),
            }).map((_, index) => (
              <ScaleButton
                aria-pressed={Number(value) === Number(question?.minimum || 1) + index}
                key={Number(question?.minimum || 1) + index}
                onClick={() => onChange(Number(question?.minimum || 1) + index)}
                selected={Number(value) === Number(question?.minimum || 1) + index}
                type="button"
              >
                {Number(question?.minimum || 1) + index}
              </ScaleButton>
            ))}
          </ScaleGrid>
        ) : question?.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE
          || question?.type === SURVEY_QUESTION_TYPES.MULTIPLE_CHOICE ? (
            <ChoiceList>
              {(question?.options || []).map(option => (
                <QuizAnswerOption
                  key={option?.id}
                  label={option?.label || option?.value}
                  multiple={question?.type === SURVEY_QUESTION_TYPES.MULTIPLE_CHOICE}
                  selected={(value || []).includes(option?.id)}
                  onSelect={() => onChange(
                    question?.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE
                      ? (value || []).includes(option?.id) ? [] : [option?.id]
                      : (value || []).includes(option?.id)
                        ? (value || []).filter(optionId => optionId !== option?.id)
                        : [...(value || []), option?.id]
                  )}
                />
              ))}
            </ChoiceList>
          ) : (
            <Input
              disabled={disabled}
              holder
              id={`survey-question-${question?.id}`}
              placeholder={placeholder}
              type={question?.type === SURVEY_QUESTION_TYPES.LONG_TEXT ? 'textarea' : 'text'}
              value={value || ''}
              onChange={event => onChange(event.target.value)}
            />
          )}
      </FieldBody>

      {!error ? null : <ErrorText role="alert">{error}</ErrorText>}
    </QuestionFieldset>
  )
}

SurveyQuestion.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.string,
  focusRef: PropTypes.shape({ current: PropTypes.any }),
  onChange: PropTypes.func.isRequired,
  optionalLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  question: PropTypes.shape({
    id: PropTypes.number,
    maximum: PropTypes.number,
    minimum: PropTypes.number,
    options: PropTypes.array,
    required: PropTypes.bool,
    text: PropTypes.string,
    type: PropTypes.string,
  }),
  requiredLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number,
    PropTypes.string,
  ]),
}

SurveyQuestion.defaultProps = {
  disabled: false,
  error: null,
  focusRef: null,
  question: null,
  value: undefined,
}
